import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import { IconHistoryOutline18 as History } from "nucleo-ui-outline-18"

import {
	Badge,
	Button,
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { api } from "@/lib/api"
import {
	formatArgsJson,
	hasTemplateTokens,
	summarizeArgs,
} from "./mcp-activity-json"
import { settingsNavGroupClass, SettingsNavRow } from "./settings-nav-row"

const OUTCOME_LABELS: Record<string, string> = {
	ok: "OK",
	tool_error: "Failed",
	unknown_tool: "Unknown tool",
	scope_denied: "Scope denied",
	forwarder_error: "Failed",
	policy_refused: "Refused by policy",
}

// Matches the retention the prune job enforces on the underlying logs.
const RETENTION_DAYS = 14

type ActivityRow =
	RouterOutputs["mcpActivity"]["workspaceActivity"]["rows"][number]

const MAX_ROWS = 200

export function McpActivityList() {
	const query = api.mcpActivity.workspaceActivity.useQuery({
		days: RETENTION_DAYS,
		limit: MAX_ROWS,
		direction: "outbound",
	})
	const rows = query.data?.rows ?? []

	if (rows.length === 0) {
		return (
			<div className={settingsNavGroupClass}>
				<div className="px-4 py-8 text-center text-sm text-secondary">
					No tool calls in the last 14 days.
				</div>
			</div>
		)
	}

	return (
		<div className={settingsNavGroupClass}>
			{rows.map((row) => (
				<ActivityRowItem key={row.id} row={row} />
			))}
		</div>
	)
}

// The eyecatcher on the Connectors page: carries the live counts and links to the full history.
export function McpActivityNavRow() {
	// Counted server-side per connector; the activity list caps its rows, so its
	// counts only ever covered the page it returned.
	const query = api.mcpActivity.connectorSummary.useQuery({
		days: RETENTION_DAYS,
	})
	const total = (query.data ?? []).reduce((sum, row) => sum + row.calls, 0)
	const failed = (query.data ?? []).reduce((sum, row) => sum + row.failed, 0)

	return (
		<div className={settingsNavGroupClass}>
			<SettingsNavRow
				icon={History}
				title="Tool call history"
				description={
					total === 0
						? "Every tool the agent called, with its arguments"
						: `${total.toLocaleString()} ${total === 1 ? "call" : "calls"} in the last 14 days${failed > 0 ? ` · ${failed.toLocaleString()} failed` : ""}`
				}
				link="/settings/custom-connectors/history"
			/>
		</div>
	)
}

function ActivityRowItem({ row }: { row: ActivityRow }) {
	const [open, setOpen] = useState(false)
	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50"
			>
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 items-center gap-1.5">
						<span className="truncate font-mono text-sm font-medium text-primary">
							{row.toolName}
						</span>
						{row.connectorTitle && (
							<Badge variant="dark-gray">{row.connectorTitle}</Badge>
						)}
						{row.variant ? (
							<Badge variant="light-gray">{row.variant.label}</Badge>
						) : (
							row.guarded && <Badge variant="light-gray">Guarded</Badge>
						)}
					</div>
					<div className="truncate font-mono text-xs text-secondary">
						{summarizeArgs(row.agentArgs ?? row.args, row.argKeys)}
					</div>
					{row.detail && (
						<div className="truncate text-xs text-red-600 dark:text-red-400">
							{row.detail}
						</div>
					)}
				</div>
				<span className="flex-none text-xs text-tertiary">
					{formatDistanceToNow(new Date(row.createdAt), { addSuffix: true })}
					{row.durationMs === null ? "" : ` · ${row.durationMs} ms`}
				</span>
				<Badge variant={row.outcome === "ok" ? "green" : "red"} size="sm">
					{OUTCOME_LABELS[row.outcome] ?? row.outcome}
				</Badge>
			</button>
			<ActivityDetailSheet row={row} open={open} onOpenChange={setOpen} />
		</>
	)
}

function ActivityDetailSheet({
	row,
	open,
	onOpenChange,
}: {
	row: ActivityRow
	open: boolean
	onOpenChange: (next: boolean) => void
}) {
	const navigate = useNavigate()

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col sm:max-w-xl">
				<SheetHeader>
					<SheetTitle className="flex items-center gap-2">
						<span className="truncate font-mono text-sm">{row.toolName}</span>
						<Badge variant={row.outcome === "ok" ? "green" : "red"} size="sm">
							{OUTCOME_LABELS[row.outcome] ?? row.outcome}
						</Badge>
					</SheetTitle>
				</SheetHeader>

				<div className="min-h-0 flex-1 overflow-y-auto pb-6">
					<dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-xs">
						{row.connectorTitle && (
							<Detail label="Connector">{row.connectorTitle}</Detail>
						)}
						{row.variant && (
							<Detail label="Variant">{row.variant.label}</Detail>
						)}
						{!row.variant && row.guarded && (
							<Detail label="Guard">Applied</Detail>
						)}
						<Detail label="When">
							{new Date(row.createdAt).toLocaleString()}
						</Detail>
						{row.durationMs !== null && (
							<Detail label="Duration">{row.durationMs} ms</Detail>
						)}
						{row.statusCode !== null && (
							<Detail label="HTTP status">{row.statusCode}</Detail>
						)}
						{row.requestId && (
							<Detail label="Request id">
								<span className="font-mono">{row.requestId}</span>
							</Detail>
						)}
						{row.injected.length > 0 && (
							<Detail label="Injected">
								<span className="font-mono">{row.injected.join(", ")}</span>
							</Detail>
						)}
						{row.transformed.length > 0 && (
							<Detail label="Transformed">
								<span className="font-mono">{row.transformed.join(", ")}</span>
							</Detail>
						)}
						{row.fallbackSteps.length > 0 && (
							<Detail label="Fallbacks">
								{row.fallbackSteps
									.map((step) => `${step.path} (${step.step}/${step.of})`)
									.join(", ")}
							</Detail>
						)}
					</dl>

					{row.detail && (
						<p className="mt-4 text-xs text-red-600 dark:text-red-400">
							{row.detail}
						</p>
					)}

					{row.agentArgs ? (
						<ArgsBlock
							title="Agent input"
							args={row.agentArgs}
							argKeys={row.argKeys}
						/>
					) : null}

					<ArgsBlock
						title={row.agentArgs ? "Sent" : "Arguments"}
						hint={argsHint(row)}
						args={row.args}
						argKeys={row.argKeys}
					/>

					{row.threadId && (
						<Button
							variant="outline"
							className="mt-5"
							onClick={() => {
								if (!row.threadId) return
								void navigate({
									to: "/threads/$id",
									params: { id: row.threadId },
								})
							}}
						>
							Open thread
						</Button>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}

function ArgsBlock({
	title,
	hint,
	args,
	argKeys,
}: {
	title: string
	hint?: string
	args: unknown
	argKeys: string[]
}) {
	return (
		<div className="mt-5">
			<div className="text-xs font-medium text-primary">{title}</div>
			{hint && <p className="mt-0.5 text-xs text-secondary">{hint}</p>}
			{args ? (
				<pre className="mt-2 overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-xs leading-relaxed text-primary dark:bg-gray-800">
					{formatArgsJson(args)}
				</pre>
			) : (
				<p className="mt-2 text-xs text-secondary">
					{argKeys.length > 0
						? `Too large to store. Keys: ${argKeys.join(", ")}.`
						: "No arguments."}
				</p>
			)}
		</div>
	)
}

function Detail({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<>
			<dt className="text-secondary">{label}</dt>
			<dd className="min-w-0 break-words text-primary">{children}</dd>
		</>
	)
}

// Older rows stored the template instead of the values, so say so.
function argsHint(row: ActivityRow): string | undefined {
	if (!row.agentArgs && hasTemplateTokens(row.args)) {
		return "Template only. This call ran before arguments were captured."
	}
	return undefined
}
